// Client-side PDF text extraction with pdfjs-dist.
// Runs entirely in the browser so large manuals never hit Vercel's
// serverless time limits — only the extracted text chunks are uploaded.
import * as pdfjsLib from 'pdfjs-dist';
import { sanitizeText } from '../lib/chunking';

// The worker file is copied into public/ by the postinstall script
// (scripts/copy-pdf-worker.js), so it is served from the app's own origin.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

export class ExtractionCancelledError extends Error {
  constructor() {
    super('Extraction cancelled');
    this.name = 'ExtractionCancelledError';
  }
}

// Extracts text page by page.
//   onProgress(pageDone, totalPages) — called after each page
//   shouldCancel() — checked before each page; throwing point for Cancel
// Returns an array of per-page strings.
export async function extractPdfText(file, { onProgress, shouldCancel } = {}) {
  const data = await file.arrayBuffer();
  const doc = await pdfjsLib.getDocument({ data }).promise;
  try {
    const pages = [];
    for (let pageNo = 1; pageNo <= doc.numPages; pageNo++) {
      if (shouldCancel?.()) throw new ExtractionCancelledError();
      const page = await doc.getPage(pageNo);
      const textContent = await page.getTextContent();
      // Join text runs; pdf.js marks explicit line breaks with hasEOL.
      // sanitizeText strips null bytes / control chars / unpaired surrogates
      // that some PDFs carry — PostgreSQL rejects them (22P05).
      const pageText = sanitizeText(
        textContent.items.map(item => (item.str || '') + (item.hasEOL ? '\n' : ' ')).join('')
      );
      pages.push(pageText);
      page.cleanup();
      onProgress?.(pageNo, doc.numPages);
    }
    return pages;
  } finally {
    await doc.destroy().catch(() => {});
  }
}
